import type { CSSProperties } from 'react';
import { useT } from '../i18n/index.ts';

/**
 * Low-level skeleton placeholder. Renders a pulsing block on the
 * `surface-container-low` token so it reads as a "one level sunken"
 * placeholder against a `surface-container` card (DESIGN.md §4
 * "Elevation" — depth from tonal shift, not shadows).
 *
 * Tailwind's built-in `animate-pulse` (NOT our 1.5s `animate-pulse-tertiary`
 * used for live-session indicators) is the right fit here: slower,
 * neutral fade. It signals "content loading" without competing with the
 * active-session pulse dot in the sidebar.
 *
 * Width/height default to `w-full h-4`; callers override via `className`
 * for row / card heights.
 */
interface Props {
  readonly className?: string;
  readonly style?: CSSProperties;
  readonly testId?: string;
}

export function Skeleton({ className = '', style, testId }: Props): JSX.Element {
  return (
    <div
      aria-hidden="true"
      data-testid={testId}
      style={style}
      className={`animate-pulse rounded-lg bg-surface-container-low ${className}`.trim()}
    />
  );
}

/**
 * Repeating row skeleton for async table loading states. DESIGN.md §6
 * "Data tables" — rows are 12px tall with 4–8px vertical gaps; we mirror
 * that cadence so the placeholder doesn't reflow once real data arrives.
 */
interface TableSkeletonProps {
  readonly rows?: number;
  readonly rowHeight?: number;
  readonly testId?: string;
}

export function TableSkeleton({
  rows = 4,
  rowHeight = 48,
  testId,
}: TableSkeletonProps): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} style={{ height: rowHeight }} />
      ))}
      <span className="sr-only">{t('skeleton.loading')}</span>
    </div>
  );
}
